import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RichTextEditor from '../../../common/RichTextEditor';

// Size configurations for full and compact variants
const SIZE_CONFIG = {
  full: {
    padding: 2,
    headerIconSize: 20,
    backgroundColor: 'white',
    headerBackground: '#f5f7fa',
    borderColor: '#e0e0e0',
    marginBottom: 2,
    fontSize: undefined,
    buttonFontSize: undefined,
  },
  compact: {
    padding: 1.5,
    headerIconSize: 16,
    backgroundColor: '#FAFBFC',
    headerBackground: '#F3F4F6',
    borderColor: '#E5E7EB',
    marginBottom: 1,
    fontSize: '13px',
    buttonFontSize: '12px',
  },
};

/**
 * NoteForm - Reusable form component for adding/editing notes
 *
 * @param {Object} props
 * @param {'full' | 'compact'} props.variant - Size variant (default: 'full')
 * @param {string} props.content - The note content
 * @param {Function} props.onChange - Callback when content changes
 * @param {Function} props.onSave - Callback when Save/Add button is clicked
 * @param {Function} props.onCancel - Callback when Cancel button is clicked
 * @param {boolean} props.isLoading - Whether the form is in a loading state
 * @param {boolean} props.isNew - Whether this is a new note (shows "Add") or editing (shows "Save")
 * @param {boolean} props.showHeader - Whether to show the form header (default: true)
 * @param {string} props.headerTitle - Custom header title (default: "New Note")
 */
const NoteForm = ({
  variant = 'full',
  content = '',
  onChange,
  onSave,
  onCancel,
  isLoading = false,
  isNew = true,
  showHeader = true,
  headerTitle = 'New Note',
}) => {
  const config = SIZE_CONFIG[variant];

  const formContent = (
    <Box sx={{ p: config.padding }}>
      <Box sx={{ mb: 1 }}>
        <RichTextEditor
          variant="compact"
          value={content}
          onChange={onChange}
          minHeight={220}
          disabled={isLoading}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={!content?.trim() || isLoading}
          sx={config.buttonFontSize ? { fontSize: config.buttonFontSize } : undefined}
        >
          {isNew ? 'Add' : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          disabled={isLoading}
          sx={config.buttonFontSize ? { fontSize: config.buttonFontSize } : undefined}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  // For editing (not new), just return the form content without wrapper
  if (!isNew && !showHeader) {
    return formContent;
  }

  // For new notes or when header is requested, wrap in Paper with header
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${config.borderColor}`,
        borderRadius: '4px',
        mb: config.marginBottom,
        overflow: 'hidden',
        backgroundColor: config.backgroundColor,
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: config.padding,
            backgroundColor: config.headerBackground,
            borderBottom: `1px solid ${config.borderColor}`,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: '#6B7280', fontSize: config.headerIconSize }} />
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.secondary"
              sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
            >
              {headerTitle}
            </Typography>
          </Box>
          <KeyboardArrowUpIcon sx={{ fontSize: config.headerIconSize }} />
        </Box>
      )}
      {formContent}
    </Paper>
  );
};

export default NoteForm;
